import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { generateCalendarLinks } from '../Utils/CalendarLinks';

const {
  FiX, FiCheck, FiCalendar, FiClock, FiUser,
  FiMail, FiDownload, FiShield, FiDollarSign, FiInfo,
} = FiIcons;

/**
 * Normalise a webinar time string into a format that Date() can parse.
 * Handles both 24-hr ("14:30") and 12-hr ("2:30 PM") formats.
 */
const normaliseTime = (timeStr = '') => {
  if (!timeStr) return '';
  // Already 24-hr  →  return as-is
  if (/^\d{1,2}:\d{2}$/.test(timeStr.trim())) return timeStr.trim();
  // 12-hr with AM/PM
  const m = timeStr.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return timeStr.trim();
  let h = parseInt(m[1], 10);
  const min = m[2];
  const ampm = m[3].toUpperCase();
  if (ampm === 'PM' && h !== 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${min}`;
};

const WebinarRegistrationModal = ({ isOpen, onClose, webinar, onRegistered }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess]       = useState(false);
  const [email, setEmail]               = useState('');

  /**
   * Build calendar links once — memoised so they don't recompute on every
   * keystroke in the email field. Uses normaliseTime() so the date string
   * fed into generateCalendarLinks is always parseable.
   */
  const calendarLinks = useMemo(() => {
    if (!webinar) return { googleUrl: '#', outlookUrl: '#', icalData: '' };
    const normalisedTime = normaliseTime(webinar.time);
    const dateStr = webinar.date && normalisedTime
      ? `${webinar.date}T${normalisedTime}`
      : webinar.date || '';
    return generateCalendarLinks({
      title:       webinar.title       || 'Webinar',
      date:        dateStr,
      location:    'OneFinalLegacy Private Lobby',
      description: webinar.description || '',
    });
  }, [webinar]);

  if (!isOpen || !webinar) return null;

  const isFree = webinar.monetization?.isFree !== false;

  const handleRegister = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      onRegistered(webinar.id);
      setTimeout(() => {
        setIsSuccess(false);
        setEmail('');
        onClose();
      }, 4000);
    }, 1500);
  };

  const downloadIcal = () => {
    try {
      const blob = new Blob([calendarLinks.icalData], { type: 'text/calendar;charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = 'webinar.ics';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* silently fail — calendar download is non-critical */
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-start sm:items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-xl overflow-hidden border border-brand-dark my-4 sm:my-8"
      >
        {/* Header */}
        <div className="p-8 border-b border-brand-light flex justify-between items-center bg-brand-light/30">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-accent-gold text-white rounded-2xl flex items-center justify-center shadow-lg">
              <SafeIcon icon={FiCalendar} className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-2xl font-serif text-accent-navy">Session Registration</h3>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Secure your sanctuary seat</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full text-text-muted transition-colors">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        <div className="p-8">
          <AnimatePresence mode="wait">
            {!isSuccess ? (
              <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {/* Session Info */}
                <div className="mb-6 p-6 bg-brand-light rounded-3xl border border-brand-dark">
                  <h4 className="font-serif text-xl text-accent-navy mb-2">{webinar.title}</h4>
                  <div className="flex flex-wrap gap-4 text-[10px] font-black uppercase tracking-widest text-accent-gold">
                    <span className="flex items-center gap-1.5"><SafeIcon icon={FiCalendar} /> {webinar.date || '—'}</span>
                    <span className="flex items-center gap-1.5"><SafeIcon icon={FiClock} /> {webinar.time || '—'}</span>
                    <span className="flex items-center gap-1.5"><SafeIcon icon={FiUser} /> {webinar.speaker || '—'}</span>
                  </div>
                </div>

                {/* Monetization block */}
                {!isFree && (
                  <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiDollarSign} className="text-amber-500 w-5 h-5 shrink-0 mt-0.5" />
                      <div>
                        <h5 className="text-sm font-bold text-amber-800 mb-1">Paid Session</h5>
                        <p className="text-xs text-amber-700">
                          Price:{' '}
                          <strong>
                            {webinar.monetization?.currency ?? 'USD'}{' '}
                            {webinar.monetization?.price ?? 0}
                          </strong>
                          {(webinar.monetization?.discountPercent ?? 0) > 0 && (
                            <span className="ml-2 text-green-700 font-bold">
                              ({webinar.monetization.discountPercent}% early bird discount applied)
                            </span>
                          )}
                        </p>
                        {webinar.monetization?.hasReplay && (
                          <p className="text-xs text-green-700 font-bold mt-1 flex items-center gap-1">
                            <SafeIcon icon={FiCheck} className="w-3 h-3" /> Replay access included
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {isFree && (
                  <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-2xl flex items-center gap-3">
                    <SafeIcon icon={FiInfo} className="text-green-600 w-4 h-4 shrink-0" />
                    <p className="text-xs text-green-700 font-bold">This session is completely free to attend.</p>
                  </div>
                )}

                <form onSubmit={handleRegister} className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Your Email Address</label>
                    <div className="relative">
                      <SafeIcon icon={FiMail} className="absolute left-5 top-1/2 -translate-y-1/2 text-text-muted" />
                      <input
                        required
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="w-full pl-12 pr-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none font-bold transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary py-5 text-lg flex items-center justify-center gap-3 shadow-xl"
                  >
                    {isSubmitting ? (
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        {isFree ? 'Confirm Attendance' : 'Proceed to Payment'}
                        <SafeIcon icon={FiCheck} />
                      </>
                    )}
                  </button>
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-6"
              >
                <div className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
                  <SafeIcon icon={FiCheck} className="w-12 h-12" />
                </div>
                <h4 className="text-3xl font-serif text-accent-navy mb-4">You're Registered!</h4>
                <p className="text-sm text-text-muted font-light leading-relaxed mb-10">
                  A secure access link has been sent to your email. Add to your calendar below.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => window.open(calendarLinks.googleUrl, '_blank')}
                    className="btn-secondary py-3 text-xs uppercase tracking-widest flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiCalendar} /> Google
                  </button>
                  <button
                    onClick={downloadIcal}
                    className="btn-secondary py-3 text-xs uppercase tracking-widest flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiDownload} /> iCal
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="bg-brand-light p-6 flex justify-center items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-text-muted">
          <SafeIcon icon={FiShield} className="w-4 h-4 text-accent-gold" /> End-to-End Encrypted Access
        </div>
      </motion.div>
    </div>
  );
};

export default WebinarRegistrationModal;